/**
 * 桌面的啟動器。
 *
 * 注意這個檔案「不知道」什麼——它不知道時間軸、代理人、閘門各自在做什麼，
 * 甚至不知道有哪些 app：那份清單是開機時抓來的。能力來自 app，核心不成長。
 *
 * 目錄改成外部檔案，是為了上線之後**換一份 JSON 就能改目錄，不用重新打包**。
 */

import { Bus } from '@qdesktop/kernel'
import type { CatalogEntry, GateLevel, SessionSnapshot } from '@qdesktop/kernel'
import { Shell, defaultSession } from '@qdesktop/shell'
import type { Account, SessionStore } from '@qdesktop/shell'

/**
 * 用 Google 登入時的回程 scheme。跟 AndroidManifest 的 intent-filter 是同一個字。
 *
 * 密碼登入不需要它——那一段整段在 app 自己的 WebView 裡跑完。**只有 Google 那條
 * 一定要離開**：Google 的登入服務刻意拒絕在 WebView 裡渲染，擋的正是「一個 app
 * 假裝成 Google 的登入頁」那種釣魚。所以那顆按鈕在 app 裡根本不會出現，而繞過它
 * 不是選項——那個限制存在的理由是對的。
 *
 * 於是這條路只給它走：開系統瀏覽器（那裡 Google 願意渲染），完成之後把兌換券送
 * 回這個 scheme。券九十秒失效、只能用一次，所以跨過那條界線的仍然不是憑證。
 *
 * ## 為什麼宣告在檔案最前面
 *
 * 這支模組有好幾條**會停住整個模組本體**的路：沒有指定桌面、要求登入、設定檔讀
 * 不到——它們在頂層丟出 PermanentStop。停在那裡之後，底下每一個 const 都不會被
 * 初始化，而那些畫面上的按鈕晚一點才去讀它們。
 *
 * 這一行原本在檔案後段，於是 Google 那顆按鈕送出的是 `undefined://paired`。同一
 * 種錯今天已經出現三次（元件的 WEEKDAYS、建置腳本的 EMBEDDERS，還有這個），而
 * 它們的共同點是：**先執行的程式讀了後宣告的值**。
 */
const APP_SCHEME = 'qdesktop'

/**
 * 從系統瀏覽器回 app 那一段。
 *
 * 用 https（Universal Links）而不是 `qdesktop://` 自訂協定，因為 SFSafariViewController
 * 對自訂協定會彈一個「Open in QDesktop?」的確認框；那個確認框沒辦法關掉，也不
 * 是「使用者主動決定」的那種確認——他們本來就是為了登入才走進 Safari 面板的，
 * 中間跳出一句「要不要回這個 app」是把「這條路是為誰走的」問了兩次。
 *
 * Universal Links 需要三件事對齊：
 *   1. `qdesktop.dynara.io/.well-known/apple-app-site-association` 列出這條路徑
 *      的 appID（見 `demo/.well-known/`）
 *   2. iOS 那半 `App.entitlements` 有 `applinks:qdesktop.dynara.io`
 *   3. `AppDelegate.swift` 的 `application(_:continue:)` 接住它
 * 三件事**全部**都是齊的（見 mobile/README.md 的清單）。iOS 認得這是這個 app
 * 的網址，Safari 面板就會**自動關掉、直接進 app**，中間一個問句都沒有。
 */
const HANDOFF_RETURN = 'https://qdesktop.dynara.io/paired'

/*
  用 Google 登入回來的那一趟。**註冊在最前面。**

  它本來寫在 requireLogin() 裡，而那支函式在第一次啟動時根本不會被呼叫——沒有指
  定桌面的時候 refuse() 就把模組停住了。於是 app 被 intent 叫醒、Capacitor 說
  「No listeners found for event appUrlOpen」，券掉在地上，畫面停在原地。

  而這件事最需要它的時刻，正好就是那一刻：**第一次連結**。

  （這個坑今天第四次。共同點都是「先執行的程式需要後宣告的東西」，見 APP_SCHEME
  上面那段。這一次不是讀到 undefined，是根本沒登記——同一個因，另一種果。）
*/
{
  const plugins = nativeShell()
  plugins?.App?.addListener('appUrlOpen', ({ url: opened }) => {
    let code = ''
    let which = ''
    try {
      const back = new URL(opened)
      code = back.searchParams.get('qd_pair') ?? ''
      which = back.searchParams.get('d') ?? ''
    } catch {}
    if (!code) return
    const again = new URL(location.href)
    again.searchParams.set('qd_pair', code)
    if (which) again.searchParams.set('d', which)
    // 讓這一輪回呼先走完再換頁。就地 replace 的話，頁面會在原生橋還在派送事件的
    // 途中卸載，而它下一步要碰的東西已經不存在了。
    setTimeout(() => location.replace(again.href), 0)
  })
}


interface DesktopConfig {
  /** 任何合法的 CSS background 值，漸層也可以 */
  wallpaper?: string
  /** 風險閘門門檻。核心預設 off——什麼算危險是使用者的政策 */
  gate_level?: GateLevel
  apps: CatalogEntry[]
  /** 每次啟動都要在的 app_id。uid 固定為 app_id，agent 才能直接定址 */
  system?: string[]
  /** 沒有存檔時才會開的 app_id */
  defaults?: string[]
  /**
   * 要求先登入 QSyn 才進得了桌面。給的是登入閘那一頁的網址。
   *
   * 這是**這一份 shell 的部署選擇**，不是核心的能力。kernel 與 shell 兩包對
   * QSyn 依然一無所知（concept.md §9），閘只活在這個檔案裡；沒設定這一項，
   * 桌面就跟以前一樣是純前端、零後端、開了就能用。
   */
  require_login?: string
}

const root = document.getElementById('desktop')
if (!root) throw new Error('找不到 #desktop')

/** QSyn 方案允許的元件視窗數。-1＝不限（沒有登入閘時就是這樣）。 */
let windowLimit = -1

interface GateReply {
  ok?: boolean
  reason?: string
  rev?: number
  snapshot?: string
  /** 方案允許的元件視窗數，-1＝不限。跟著快照一起回來。 */
  widget_limit?: number
}

/**
 * 問閘門一件需要 QSyn 憑證的事，等它回答。
 *
 * 只有登入閘存在時才有這個東西——沒有閘門就沒有 device token，也就沒有地方
 * 可以存。
 */
let askGate: ((request: Record<string, unknown>) => Promise<GateReply | null>) | null = null

/**
 * 開機畫面用的當下語言。原生殼在 document_start 把選好的語言塞進 `window.
 * __qdesktopInitialLocale`；web 版沒這條，退回 `navigator.language`。開機字串
 * 都在 shell 起動前跑，這裡不能靠 shell 的翻譯機制，只能自己一份小的。
 */
type BootLocale = 'zh-Hant' | 'en'
const bootLocale: BootLocale = (() => {
  const inj = (window as { __qdesktopInitialLocale?: unknown }).__qdesktopInitialLocale
  if (inj === 'zh-Hant' || inj === 'en') return inj
  return (navigator.language ?? '').toLowerCase().startsWith('zh') ? 'zh-Hant' : 'en'
})()
const bt = <T extends Record<BootLocale, string>>(m: T): string => m[bootLocale]

/**
 * 開機畫面的字。
 *
 * 這幾段等待都是**別人的網路**：QSyn 說這個人是誰、把桌面存檔取回來。說出現在
 * 在等哪一段，卡住的時候才看得出是卡在哪裡——一個不動的轉圈只說得出「還沒好」。
 *
 * 只改字，不動那條長條：它刻意是來回掃的，因為這裡沒有辦法知道還要多久，而一條
 * 假裝知道進度的長條停在 90% 的時候比沒有更難看。
 */
function booting(text: string): void {
  const step = document.getElementById('boot-step')
  if (step) step.textContent = text
}

/**
 * 沒有桌面 id 時停在開機畫面上，並指路。
 *
 * 這個函式**不回來**：後面每一件事都需要知道是哪一個桌面，繼續跑只會做出一個
 * 假的。停在這裡而不是丟例外，是因為畫面上要留一句人看得懂的話和一個連結。
 */
/*
  兩顆登入按鈕，還有一句話說清楚它們差在哪。

  舊的版本是一顆藍的寫「連結 QSyn」、一顆灰的寫「用 Google 登入（會開瀏覽器）」。
  問題不是難看而已：那兩行字聽起來像**兩個不同的地方**，所以第一次開 app 的人
  得先猜自己要去哪一個。實際上它們是同一個地方的兩種進門方式。

  所以照大家已經認得的樣子做：一顆主要的，一顆白底的 Google（那個 G 是全世界
  都認得的形狀，比任何一句說明都快），底下一句話講完差別——一個在 app 裡走完，
  一個要出去瀏覽器一趟再回來。**會離開 app 這件事要先講**，不然回來的那一秒看
  起來像 app 自己跳掉了。
*/
function signInStyles(): void {
  if (document.getElementById('qd-signin-css')) return
  const css = document.createElement('style')
  css.id = 'qd-signin-css'
  css.textContent = [
    '.qd-signin{display:flex;flex-direction:column;align-items:center;gap:10px;',
    'width:min(340px,86vw);margin:18px auto 0;text-align:center}',
    '.qd-signin .lead{margin:0 0 8px;font:14px/1.65 system-ui;color:#ffffffa8}',
    '.qd-signin button{display:flex;align-items:center;justify-content:center;gap:10px;',
    'width:100%;height:50px;border:0;border-radius:14px;font:600 15px system-ui;cursor:pointer}',
    '.qd-signin .primary{background:#4a5bd4;color:#fff}',
    '.qd-signin .primary:active{background:#4050bb}',
    '.qd-signin .note{margin:6px 0 0;font:12px/1.7 system-ui;color:#ffffff5c}',
  ].join('')
  document.head.append(css)
}

/**
 * 那一片「請先登入」。兩個地方用同一份：第一次開 app（refuse）與閘門說不行
 * （loginScreen）——那本來就是同一件事。
 *
 * **一顆按鈕**，因為登入這件事全交給 QSyn 的登入頁去做：密碼、Google、忘記密
 * 碼、註冊，那些選項那一頁本來就都有，Safari 又原生支援 iCloud Keychain 的
 * autofill，殼裡再刻一份等於重蓋一次那些能力、還蓋得比較差。原生殼裡按下去
 * 開系統瀏覽器（`Browser.open`），登完靠 `qdesktop://paired` 這條自訂協定回
 * 程；網頁版就地跳去 QSyn（`location.assign`）。
 */
function signInPanel(lead: string): HTMLElement {
  signInStyles()
  const panel = document.createElement('div')
  panel.className = 'qd-signin'
  const native = !!nativeShell()
  panel.innerHTML =
    `<p class="lead">${lead}</p>` +
    '<button class="primary">登入 QSyn</button>' +
    (native
      ? '<p class="note">會開一次瀏覽器，登完自動回來。</p>'
      : '')
  return panel
}

function pickerStyles(): void {
  if (document.getElementById('qd-picker-css')) return
  const css = document.createElement('style')
  css.id = 'qd-picker-css'
  css.textContent = [
    '.qd-picker{display:flex;flex-direction:column;gap:8px;width:min(340px,86vw);',
    'margin:18px auto 0}',
    '.qd-picker .lead{margin:0 0 6px;font:14px/1.65 system-ui;color:#ffffffa8;text-align:center}',
    '.qd-picker .row{display:flex;align-items:center;justify-content:space-between;',
    'padding:14px 16px;background:#ffffff08;border:1px solid #ffffff14;border-radius:12px;',
    'color:#e8e8ef;font:500 15px system-ui;cursor:pointer;text-align:left}',
    '.qd-picker .row:active{background:#ffffff14}',
    '.qd-picker .row[disabled]{opacity:.5;cursor:default}',
    '.qd-picker .name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '.qd-picker .chev{color:#ffffff55;font-size:20px;flex:none;margin-left:12px}',
    '.qd-picker .empty{margin:12px 0 0;font:14px/1.7 system-ui;color:#ffffff70;text-align:center}',
    '.qd-picker .link{display:inline-block;margin:14px auto 0;padding:12px 18px;',
    'background:#4a5bd4;color:#fff;border-radius:12px;font:600 14px system-ui;text-decoration:none}',
    '.qd-picker .link:active{background:#4050bb}',
    '.qd-picker .swap{margin:14px auto 0;background:transparent;border:0;color:#ffffff55;',
    'font:12.5px system-ui;cursor:pointer;padding:6px 10px}',
    '.qd-picker .swap:active{color:#ffffffa0}',
  ].join('')
  document.head.append(css)
}

/**
 * 用一個藏起來的 gate iframe 去問「這個帳號有哪些桌面」。
 *
 * 殼自己拿不到 QSyn 的 JWT（那把住在 QSyn 那個來源的 localStorage 裡，跨來源
 * 讀不到）——但 gate iframe 就在那個來源上，讀得到，也可以照樣打 API。所以殼
 * 提出問題，iframe 回答，跟其他 session op 是同一個橋。
 *
 * 拿不到就回 null——不是空清單，那兩個意義不一樣（一個是「沒登入」，一個是
 * 「登入了但沒建桌面」），畫面上要說的話也是兩句。
 */
async function tryListDesktops(qsynOrigin: string): Promise<{ id: string; name?: string }[] | null> {
  const gateUrl = new URL(`${qsynOrigin}/qdesktop-agent/gate.html`)
  gateUrl.searchParams.set('desktop', location.origin)
  const gateOrigin = gateUrl.origin

  const frame = document.createElement('iframe')
  frame.src = gateUrl.href
  frame.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden'
  document.body.append(frame)

  try {
    await new Promise<void>((resolve) => {
      frame.addEventListener('load', () => resolve(), { once: true })
    })
    const reply = await new Promise<{ ok?: boolean; desktops?: { id: string; name?: string }[] } | null>((settle) => {
      const timer = setTimeout(() => {
        removeEventListener('message', onMessage)
        settle(null)
      }, 8000)
      const onMessage = (event: MessageEvent) => {
        if (event.origin !== gateOrigin) return
        const data = event.data as { qsyn_gate?: number; reply_to?: number; ok?: boolean; desktops?: { id: string; name?: string }[] } | null
        if (!data || data.qsyn_gate !== 1 || data.reply_to !== 1) return
        clearTimeout(timer)
        removeEventListener('message', onMessage)
        settle(data)
      }
      addEventListener('message', onMessage)
      frame.contentWindow?.postMessage(
        { qsyn_gate_op: 'desktops.list', id: 1 },
        gateOrigin,
      )
    })
    if (!reply?.ok) return null
    return reply.desktops ?? []
  } finally {
    frame.remove()
  }
}

async function refuse(qsynOrigin: string): Promise<never> {
  const step = document.getElementById('boot-step')
  const bar = document.querySelector<HTMLElement>('#boot .bar')
  if (bar) bar.style.display = 'none'
  const shell = nativeShell()
  const boot = document.getElementById('boot')
  if (!shell || !boot) {
    /* 網頁版：沒挑桌面就給一個連結去 QSyn 挑。 */
    if (step) {
      step.textContent = '沒有指定要開哪一個桌面。'
      const link = document.createElement('a')
      link.href = `${qsynOrigin}/dashboard/desktops`
      link.textContent = '到 QSyn 的桌面清單挑一個 →'
      link.style.cssText = 'display:block;margin-top:10px;color:#7aa2ff;text-decoration:none'
      step.append(link)
    }
    throw new PermanentStop()
  }

  if (step) step.textContent = ''

  /*
    第一次開 app 的人走這條路：手上什麼都沒有——沒有 QSyn 憑證、沒有挑好的桌
    面。以前這裡直接顯示登入按鈕，等使用者到 Safari 面板挑桌面再回來；現在如
    果我們**其實已經有 session**（登入後 ?s= 交接回來、只是還沒挑桌面），就
    直接在 app 裡列桌面讓他挑，不用再去一趟 Safari 面板。

    先問 gate iframe：有 session 嗎？有的話清單也一起回來。沒有的話跟以前一樣
    給登入按鈕，那顆按鈕再跳 Safari 走登入流程，回程再走一次這條路。
  */
  const desktops = await tryListDesktops(qsynOrigin)

  if (desktops === null) {
    const panel = signInPanel('這台裝置還沒有連上桌面。登入後挑一個。')
    const primary = panel.querySelector('button')
    primary?.addEventListener('click', () => {
      const next = encodeURIComponent(HANDOFF_RETURN)
      void shell.Browser?.open({ url: `${qsynOrigin}/desktop/link?next=${next}` })
    })
    boot.append(panel)
    throw new PermanentStop()
  }

  pickerStyles()
  const panel = document.createElement('div')
  panel.className = 'qd-picker'
  const lead = document.createElement('p')
  lead.className = 'lead'
  lead.textContent = desktops.length === 0 ? '這個帳號還沒有桌面。' : '挑一個桌面'
  panel.append(lead)

  if (desktops.length === 0) {
    const link = document.createElement('a')
    link.className = 'link'
    link.href = `${qsynOrigin}/dashboard/desktops`
    link.textContent = '到 QSyn 建一個 →'
    link.addEventListener('click', (e) => {
      e.preventDefault()
      void shell.Browser?.open({ url: `${qsynOrigin}/dashboard/desktops` })
    })
    panel.append(link)
  } else {
    for (const d of desktops) {
      const row = document.createElement('button')
      row.type = 'button'
      row.className = 'row'
      const name = document.createElement('span')
      name.className = 'name'
      name.textContent = d.name && d.name.trim() ? d.name : '未命名桌面'
      const chev = document.createElement('span')
      chev.className = 'chev'
      chev.textContent = '›'
      row.append(name, chev)
      row.addEventListener('click', () => {
        for (const b of panel.querySelectorAll<HTMLButtonElement>('button.row')) b.disabled = true
        const back = new URL(location.href)
        back.searchParams.set('d', d.id)
        location.replace(back.href)
      })
      panel.append(row)
    }
  }

  const swap = document.createElement('button')
  swap.type = 'button'
  swap.className = 'swap'
  swap.textContent = '用另一個帳號登入 →'
  swap.addEventListener('click', () => {
    const next = encodeURIComponent(HANDOFF_RETURN)
    void shell.Browser?.open({ url: `${qsynOrigin}/desktop/link?next=${next}` })
  })
  panel.append(swap)
  boot.append(panel)

  // 停住。這個模組是頂層 await，永遠不 resolve 的 promise 就是「到此為止」。
  throw new PermanentStop()
}

/** 不是錯誤，是刻意停下來。全域的錯誤處理器認得它，才不會再蓋一層紅字上去。 */
class PermanentStop extends Error {}

/** 開機畫面退場。淡出交給 CSS，結束之後才移除，不然會看到它瞬間消失。 */
function booted(): void {
  const boot = document.getElementById('boot')
  if (!boot) return
  boot.classList.add('gone')
  boot.addEventListener('transitionend', () => boot.remove(), { once: true })
  // transitionend 不保證會來（分頁在背景時動畫不跑），所以留一條退路
  setTimeout(() => boot.remove(), 1200)
}

/**
 * 開不起來的時候要說話。
 *
 * 這是頂層 await 的模組：中途拋出例外的話，整個模組就停在那裡，而畫面上留著
 * 一個永遠「啟動中…」的開機畫面。一個不會結束的載入動畫是最糟的失敗樣子——
 * 它看起來像還有救，所以人會一直等。
 */
for (const kind of ['error', 'unhandledrejection'] as const) {
  addEventListener(kind, (event: Event) => {
    const cause = (event as ErrorEvent).error ?? (event as PromiseRejectionEvent).reason
    // 刻意停下來的那一種已經把話說完了，不要再蓋一層「開不起來」上去。
    if (cause instanceof PermanentStop) {
      // 也吃掉預設行為，瀏覽器的主控台才不會印一行 Uncaught Error。每次開機都出
      // 現的錯誤，教人的是忽略錯誤——下一個真的壞掉的時候就混在裡面了。
      //
      // 原生殼裡仍然會有一行：Capacitor 自己掛了一個錯誤處理器往 logcat 記，
      // preventDefault 管不到它。那一行只在 debug 版的日誌裡，使用者看不到，所以
      // 就留著——為了它去動殼的程式碼，代價比那行字高。
      event.preventDefault()
      return
    }
    const step = document.getElementById('boot-step')
    if (!step || step.dataset['failed']) return
    step.dataset['failed'] = '1'
    step.textContent = `開不起來：${cause instanceof Error ? cause.message : String(cause)}`
    step.style.color = '#ff9a90'
    step.style.opacity = '1'
    document.querySelector<HTMLElement>('#boot .bar')?.style.setProperty('display', 'none')
  })
}

booting(bt({ 'zh-Hant': '讀取設定…', en: 'Loading configuration…' }))
const config = await loadConfig()
/**
 * 沒有指名桌面就不開。
 *
 * 桌面現在是有身分的東西：存去哪裡、代理人叫得到哪一個，都靠那個 id。沒有它
 * 的話畫面照樣長得出來，但**存不到任何人以為它會存去的地方**，而且沒有代理人
 * 碰得到——一個看起來正常、實際上什麼都接不上的桌面，比一句「請從清單開啟」
 * 糟得多。
 *
 * 這跟「QSyn 掛掉時桌面照開」（D33）不衝突：那是依賴失效，這是缺必要輸入。
 * 把兩者混為一談，才會做出那個無名桌面。
 *
 * 沒有設登入閘的部署不受影響——那種部署裡本來就沒有桌面清單這種東西，
 * QDesktop 仍然是一個能獨立跑的站。
 */
/**
 * 從主畫面啟動的時候，網址上不會有 `?d=`。
 *
 * 一個裝起來的 PWA 只知道它的 start_url，而那裡沒有辦法寫死是哪一個桌面——所以
 * 沒有這一段的話，點開圖示看到的永遠是「沒有指定要開哪一個桌面」。
 *
 * 記住最後一次開的那一個，下次就直接開它。用 replace 不用 assign：這一次沒有
 * `d` 的那筆歷史不該留著，不然按上一頁會回到同一片拒絕畫面。
 */
/*
  啟動畫面**在這裡收**，不是等桌面開完。

  收得晚一點看起來比較順——中間那段就一直是啟動畫面。但這支模組有好幾條會提早
  停住的路：沒有指定桌面、要求登入、設定檔讀不到。任何一條走到，`hide()` 就永
  遠不會被呼叫，而使用者盯著一張不會動的啟動畫面，那比閃一下白色糟得多。

  而且這裡沒有東西要遮：index.html 自己那片開機畫面是深色的、而且已經畫好了。
  真正需要遮的是 WebView 還沒接手的那一小段，那一段在這行之前就結束了。
*/
{
  const plugins = nativeShell()
  void plugins?.SplashScreen?.hide({ fadeOutDuration: 200 })
  // DARK 指的是「底下是深色，所以圖示要亮的」。
  void plugins?.StatusBar?.setStyle({ style: 'DARK' })
  // 讓網頁鋪到狀態列底下，並且把那條的底色拿掉。桌布因此鋪滿整片玻璃，而狀態
  // 膠囊與 dock 靠 CSS 的 safe-area 讓開——背景到邊、控制項避開。
  void plugins?.StatusBar?.setOverlaysWebView({ overlay: true })
  void plugins?.StatusBar?.setBackgroundColor({ color: '#00000000' })

  /*
    鍵盤吃掉多少高度，告訴桌面。

    這個殼刻意不讓鍵盤改變 WebView 的尺寸——那會讓整片桌面的視窗跟著重排，而人
    只是想打幾個字。代價是網頁那邊的 visualViewport 也不會縮，於是任何想「排在
    鍵盤上面」的東西都排錯位置（右鍵選單就是這樣被蓋住的）。

    所以由這裡說。用一個 CSS 變數而不是叫 shell 去認識 Capacitor：shell 對宿主
    是什麼一無所知（concept.md §9），而「還剩多少可用高度」正是只有宿主答得出
    來的事。改完派一個 resize，因為自訂屬性的變更沒有事件可訂。
  */
  /*
    系統列有多高。

    桌面現在鋪到狀態列與手勢列底下（那兩條是透明的），所以貼著邊的控制項會被壓
    住。`env(safe-area-inset-*)` 幫不上忙——它量的是**螢幕的瀏海與圓角**，系統列
    不算在裡面，沒有瀏海的機器上它就是 0。

    24dp 是 Android 狀態列與手勢列的標準高度，不是猜的。有瀏海的機器上 env() 會
    更大，而 shell 那邊取的是兩者的大值，所以這個數字只是地板。
  */
  document.documentElement.style.setProperty('--qd-inset-top', '24px')
  document.documentElement.style.setProperty('--qd-inset-bottom', '24px')

  const keyboard = (px: number): void => {
    document.documentElement.style.setProperty('--qd-keyboard', `${Math.max(0, px)}px`)
    dispatchEvent(new Event('resize'))
  }
  plugins?.Keyboard?.addListener('keyboardWillShow', ({ keyboardHeight }) => keyboard(keyboardHeight))
  plugins?.Keyboard?.addListener('keyboardWillHide', () => keyboard(0))
}

/*
  從 app 內的 QSyn 登入頁按下「用 Google 登入」。

  那一頁在 WebView 裡畫不出 Google 官方的按鈕（Google 刻意擋的），所以它把這件
  事丟回來：**只有 app 叫得動系統瀏覽器**。這裡開的就是第一次連結時那條路，回程
  也一樣是 qdesktop://paired。

  不停下來。瀏覽器蓋在上面，底下這個桌面該長什麼樣就長什麼樣——已經配對的就是
  桌面，還沒配對的就是那片登入畫面。人在瀏覽器裡按取消回來時，看到的是原本的地
  方，而不是一片為了這件事特地留著的空白。
*/
if (config.require_login && new URLSearchParams(location.search).get('qd_google')) {
  const shell = nativeShell()
  // 從網址上抹掉，否則重新整理會再開一次瀏覽器。
  const clean = new URL(location.href)
  clean.searchParams.delete('qd_google')
  history.replaceState(null, '', clean.href)
  const next = encodeURIComponent(HANDOFF_RETURN)
  void shell?.Browser?.open({
    url: `${new URL(config.require_login).origin}/desktop/link?next=${next}`,
  })
}

/*
  網址上有一張**登入狀態**的券（?s=）。

  它是從瀏覽器交接回來的：用 Google 登入必須離開 app 一趟，於是登入狀態留在瀏
  覽器裡，而配對帶回來的只有裝置憑證——桌面開得起來，但選桌面、設定、以及會列
  代理人的那兩個元件都是以帳號的身分說話的，對它們來說這個 app 還是登出的。

  這一側換不了：券要換成 QSyn 那個來源的登入狀態，而寫得進那個來源的儲存的，只
  有跑在那個來源上的頁面。所以繞一下——去 QSyn 的 adopt 頁換完，它會照著 next 把
  人送回這裡，配對那張券還在網址上，接著走原本的路。

  **在還原桌面之前**，而且要先把 s 從網址上拿掉，否則繞回來又會再繞一次。
*/
if (config.require_login && new URLSearchParams(location.search).get('s')) {
  const params = new URLSearchParams(location.search)
  const ticket = params.get('s') ?? ''
  const back = new URL(location.href)
  back.searchParams.delete('s')
  const adopt = new URL(`${new URL(config.require_login).origin}/desktop/adopt`)
  adopt.searchParams.set('s', ticket)
  adopt.searchParams.set('next', back.href)
  location.replace(adopt.href)
  // 導頁是非同步的；不停住的話這個模組會繼續開機，而它馬上就要被換掉了。
  throw new PermanentStop()
}

const LAST_DESKTOP = 'qdesktop.last_desktop'

/*
  登出回來的那一趟。**在還原桌面之前**，否則登出完的下一秒桌面就自己開起來了。

  登出的按鈕在 QSyn 自己的頁面上。那一頁清得掉 session token，但清不掉這台裝置
  的憑證——憑證住在登入閘的 iframe 裡，瀏覽器把它分到另一個儲存格子，而那個分隔
  正是當初要做兌換券交接的原因。所以那一頁清完自己那一半，就用 qdesktop://paired
  把最後一段交回來，剩下的一半在這裡做。

  少了這一段，「登出」的結果是：桌面照常連著、照常可寫，只有元件會說「請先登入」
  ——一個半登出的狀態，比兩種都糟，而且在一支借給別人的手機上，它等於沒有登出。
*/
if (config.require_login && new URLSearchParams(location.search).get('qd_logout')) {
  booting(bt({ 'zh-Hant': '登出中…', en: 'Signing out…' }))
  let which = new URLSearchParams(location.search).get('d') ?? ''
  if (!which) {
    try {
      which = localStorage.getItem(LAST_DESKTOP) ?? ''
    } catch {}
  }
  await forgetDevice(config.require_login, which)
  try {
    localStorage.removeItem(LAST_DESKTOP)
  } catch {}
  // 乾淨的網址重新開一次：沒有 d 就會停在「還沒連上桌面」，那正是登出該有的樣子。
  const clean = new URL(location.href)
  clean.search = ''
  location.replace(clean.href)
  // 導頁是非同步的，模組不能繼續跑下去（見下面那一段同樣的理由）。
  throw new PermanentStop()
}

{
  const which = new URLSearchParams(location.search).get('d')
  if (which) {
    try {
      localStorage.setItem(LAST_DESKTOP, which)
    } catch {}
  } else if (config.require_login) {
    let remembered = ''
    try {
      remembered = localStorage.getItem(LAST_DESKTOP) ?? ''
    } catch {}
    if (remembered) {
      const back = new URL(location.href)
      back.searchParams.set('d', remembered)
      location.replace(back.href)
      // 導頁是非同步的，而這個模組是頂層 await：不停住的話它會繼續開機，
      // 在換頁的那半秒裡閃出一片拒絕畫面。
      throw new PermanentStop()
    }
  }
}

if (config.require_login && !new URLSearchParams(location.search).get('d')) {
  await refuse(new URL(config.require_login).origin)
} else if (config.require_login) {
  booting(bt({ 'zh-Hant': '確認 QSyn 登入…', en: 'Checking QSyn sign-in…' }))
}
const account = config.require_login ? await requireLogin(config.require_login) : null

const bus = new Bus()

// 把每個 app 的 title / summary 換成當下語言的版本——config.json 每一項附
// `i18n.en` 這樣的翻譯欄；shell 用的 CatalogEntry 型別沒帶那條資訊（那是**外
// 部檔案的格式**），所以在這裡就地解析並蓋回 title / summary。這樣「新增工具」
// 選單 / spec() / launch 走的都是翻好的字。
//
// 語言來源同 `injectedLocale`（見下面）——原生殼把選擇塞進 `window.__qdesktop
// InitialLocale`，這裡先讀一次；讀不到（web 版）就維持原字（多半是中文）。
{
  const overrideLocale = (window as { __qdesktopInitialLocale?: unknown })
    .__qdesktopInitialLocale
  const key =
    overrideLocale === 'en'
      ? 'en'
      : overrideLocale === 'zh-Hant'
        ? 'zh-Hant'
        : (navigator.language ?? '').toLowerCase().startsWith('zh')
          ? 'zh-Hant'
          : 'en'
  for (const app of config.apps) {
    const i18n = (app as unknown as { i18n?: Record<string, { title?: string; summary?: string }> }).i18n
    const local = i18n?.[key]
    if (local?.title) app.title = local.title
    if (local?.summary) app.summary = local.summary
  }
}

const byId = (id: string): CatalogEntry => {
  const entry = config.apps.find((candidate) => candidate.app_id === id)
  if (!entry) throw new Error(`目錄裡沒有 ${id}`)
  return entry
}

/** 目錄項目 → 啟動參數。uid 有給就是固定的，讓 agent 可以直接定址。 */
const spec = (id: string, uid?: string) => {
  const entry = byId(id)
  const window = entry.default_window
  return {
    url: entry.url,
    title: entry.title,
    summary: entry.summary,
    slug: entry.app_id,
    ...(uid ? { uid } : {}),
    ...(window?.rect ? { rect: window.rect } : {}),
    // 開機就 hidden 的 app（例如右鍵面板）要靠這個——少了它，面板會在
    // 每次開機時閃一下出現在左上角。
    ...(window?.state === undefined ? {} : { state: window.state }),
    ...(window?.z === undefined ? {} : { z: window.z }),
    ...(window?.transparent === undefined ? {} : { transparent: window.transparent }),
  }
}

/*
  回到「挑一個桌面」。

  App 裡沒有網址列，於是一台裝置連上某個桌面之後就再也回不去挑選畫面，連「我
  是不是接到別人的桌面了」都沒有辦法自己確認。三種殼各走各的路：

  - Flutter 原生殼（`flutter_inappwebview`）：呼叫 native JS handler，那邊會
    把 device token 從 Keychain 清掉、router 自動推回 picker。**憑證清除發生
    在原生層**，web shell 這半連摸都沒摸到。
  - Capacitor 殼：跟登入按鈕同一條路——`Browser.open` 開 SFSafariViewController
    到 QSyn 的 `/desktop/link`（不帶 `?d=`，列桌面清單），回程走 Universal Link
    彈回 app、由 AppDelegate 接住 `qdesktop://paired`。
  - 網頁版：沒有這顆按鈕（chip 是 span 不是 button）。瀏覽器上這條路是網址列
    ——改掉 `?d=` 就換一個桌面，而那是每個人都已經會的動作。

  用 `location.assign` 把 WebView 帶去 qsyn.dynara.io 那條路對 Capacitor 是斷
  的：WebView 那格 storage 跟 Safari 的完全分開，QSyn 的 JWT 只住在 Safari，
  於是 WebView 開到那一頁就又問一次登入——一次白繞一圈瀏覽器。
*/
interface FlutterInappwebview {
  callHandler(name: string, ...args: unknown[]): unknown
}
const flutterBridge = (globalThis as { flutter_inappwebview?: FlutterInappwebview })
  .flutter_inappwebview
const switchDesktop = !config.require_login
  ? null
  : flutterBridge
    ? () => void flutterBridge.callHandler('qdesktopSwitchDesktop')
    : nativeShell()
      ? () => {
          const gate = new URL(config.require_login as string).origin
          const next = encodeURIComponent(HANDOFF_RETURN)
          void nativeShell()?.Browser?.open({
            url: `${gate}/desktop/link?next=${next}`,
          })
        }
      : null

booting(bt({ 'zh-Hant': '還原桌面…', en: 'Restoring desktop…' }))
const shell = await Shell.start({
  bus,
  root,
  catalog: config.apps,
  session: sessionStore(),
  // 固定 uid：關掉了下次重整還會回來。
  // 閘門也算系統 app——攔截開著卻沒有核准介面，操作會卡到逾時。
  systemApps: (config.system ?? []).map((id) => spec(id, id)),
  defaults: (config.defaults ?? []).map((id) => spec(id)),
  // 「連線」在這個部署裡是指 QSyn 的裝置通道。shell 不知道那是什麼，它只是把
  // 這條 topic 的值畫成一個點——哪一條代表連線是這裡的政策。
  statusTopic: 'app/qsyn-link/status',
  // 函式而不是數字：這個上限是跟著存檔一起從 QSyn 回來的，而那次請求就發生在
  // Shell.start 內部——比還原早一步，但比這裡晚一步。
  windowLimit: () => windowLimit,
  ...(account ? { account } : {}),
  ...(switchDesktop ? { onSwitchDesktop: switchDesktop } : {}),
  // 原生殼在 document_start 塞 __qdesktopInitialLocale。有它 = 原生 app，
  // 那邊自己有「帳號 → 說明文件」入口，shell dock 就不再放重複的「?」。
  showHelp: !(window as { __qdesktopInitialLocale?: unknown }).__qdesktopInitialLocale,
})

// 宿主（原生 app）決定的介面語言 —— native 用 UserScript 在 document_start
// 把 `window.__qdesktopInitialLocale` 塞進來。存在就以它為準，覆蓋 session
// 裡存過的 locale。這樣使用者在原生「帳號 → 語言」改成英文之後，桌面裡的
// shell 跟所有元件都一致地跟著換，不會兩層講不同語言。
//
// 用 `origin: 'shell'` 而不是 `'user'` 是刻意的：這條寫入代表**宿主的選擇**，
// 概念上跟 shell 自己第一次設定的那條同一類，不是使用者剛在設定裡按了一顆按鈕。
const injectedLocale = (window as { __qdesktopInitialLocale?: unknown }).__qdesktopInitialLocale
if (injectedLocale === 'zh-Hant' || injectedLocale === 'en') {
  void bus.write('$sys/locale', injectedLocale, { origin: 'shell' })
}

// 這些是「第一次開機的預設外觀」，不是每次都套用——
// 否則使用者改過的桌布每次重整都會被蓋掉。
if (!shell.restoredFromSession) {
  // 第一次開機才跟著瀏覽器語言走（原生殼上面已經覆蓋掉了）。之後這條 topic
  // 屬於使用者——他改過的語言不該因為換了一台裝置就被瀏覽器的設定蓋掉。
  if (injectedLocale !== 'zh-Hant' && injectedLocale !== 'en') {
    const language = navigator.language?.toLowerCase() ?? ''
    void bus.write('$sys/locale', language.startsWith('zh') ? 'zh-Hant' : 'en', { origin: 'shell' })
  }
  if (config.wallpaper) {
    void bus.write('$sys/wallpaper/color', config.wallpaper, { origin: 'shell' })
  }
  if (config.gate_level) {
    void bus.write('$sys/gate/level', config.gate_level, { origin: 'shell' })
  }
}

booted()

/*
  原生殼的收尾。

  這幾件事是「像 app」與「像網頁」之間的差別，而它們沒有一件動到核心——桌面本
  身不知道自己跑在哪裡，是這裡把宿主的能力接上去。

  - **啟動畫面**：等桌面真的畫好了才收。自動收的話會露出中間那段白畫面，而那一
    閃是最像網頁的一件事。
  - **返回鍵**：Android 的返回鍵預設直接關掉 app。一個桌面上有選單、有視窗，返
    回應該先收掉最上面那一層——**先關一件事，不是關全部**。真的沒有東西可關才
    離開。
*/
{
  const plugins = nativeShell()
  if (plugins) {
    plugins.App?.addListener('backButton', () => {
      // 選單開著就先收選單。它蓋在最上面，而人按返回想收的一定是它。
      if (bus.read('$sys/menu/at')?.value !== null) {
        void bus.write('$sys/menu/at', null, { origin: 'shell' })
        return
      }
      // 其次是最上面那一個視窗。用 z 最大的那一個——那就是「最上面」的定義，
      // 不是最後開的那一個（拖過之後那兩件事就不一樣了）。
      const instances =
        (bus.read('$sys/registry/instances')?.value as { uid: string }[] | undefined) ?? []
      let top = ''
      let highest = -Infinity
      for (const { uid } of instances) {
        const state = bus.read(`app/${uid}/$window/state`)?.value
        if (state === 'hidden' || state === 'closed') continue
        const z = Number(bus.read(`app/${uid}/$window/z`)?.value ?? 0)
        if (z > highest) {
          highest = z
          top = uid
        }
      }
      if (top) {
        void bus.call('$sys/cmd/close', { uid: top }, { origin: 'shell' })
        return
      }
      // 什麼都沒有了才離開。一個「按返回就直接關掉」的 app，會在人只是想收掉一
      // 個視窗的時候把整個桌面收掉。
      plugins.App?.exitApp()
    })
  }
}


// 方便在 DevTools 主控台裡扮演 agent：
//   bus.read('$sys/registry/apps').value
//   await bus.call('$sys/cmd/launch', { app_id: 'notify' })
//   bus.sessionSnapshot()
// account 也放出來：多個桌面之後，「這個分頁到底是哪一個桌面」是查任何問題的
// 第一個問題，而它現在只活在一個閉包裡。
Object.assign(globalThis, { bus, shell, account })

/**
 * 擋在桌面前面的登入閘。
 *
 * 桌面**沒有辦法自己回答「這個人登入了沒」**——QSyn 的憑證在另一個來源的
 * localStorage 裡，同源政策擋著。所以問題外包給一個藏起來的 iframe：那一頁由
 * QSyn 託管，因此讀得到憑證，驗完把是非題的答案 postMessage 回來。
 *
 * 桌面拿到的自始至終只有是非題的答案與幾個顯示用的名字，**憑證一次都沒有過來**。
 * 這跟 bridge app 是同一個原則：碰得到 QSyn 憑證的東西只能住在 QSyn 那一側。
 *
 * 回傳身分，因為「登入了」和「誰登入了」是兩件事。以前這裡只用了 `ok`，名字
 * 收下就丟掉——於是一台桌面看起來跟另一台一模一樣，而它們可能配在不同的工作
 * 空間、屬於不同的人。
 */
/**
 * 存檔要分人。
 *
 * 桌面是**那個使用者的**桌面：同一台機器換一個人登入 QSyn，看到的該是他自己的
 * 視窗，不是上一個人的。以前所有人共用一把固定的 key，所以兩個人用同一台電腦
 * 會互相踩，而同一個人換一台電腦則什麼都沒有。
 *
 * 分 key 這件事是**宿主的政策**，不是核心的：shell 只接受一個 SessionStore，
 * 它不知道也不該知道有「使用者」這種東西。
 *
 * 沒有登入閘時照舊用那把固定的 key——沒有身分就沒有東西可以分。
 */
function sessionStore(): SessionStore {
  // IndexedDB 為主，開不起來（無痕模式）就退回 localStorage
  const legacy = defaultSession()
  if (!account?.user_id) return legacy

  // 本機快取以**桌面**為鍵，不是以人。同一個人可以有好幾個桌面，共用一把 key
  // 會讓它們互相覆蓋——那正是「多個桌面」要解決的問題本身。
  const mine = defaultSession(
    account.desktop_id
      ? `qdesktop.session:d:${account.desktop_id}`
      : `qdesktop.session:${account.user_id}`,
  )
  const local: SessionStore = {
    async load() {
      const own = await mine.load()
      if (own) return own
      // 舊存檔只給「沒有指名桌面」的那條路接收——那是一人一桌面時代的延續，
      // 分 key 的當下不接的話，所有人的桌面看起來都被清空了。
      //
      // **指名了桌面就不接。** 一個剛建好的桌面該是空的：每個新桌面都去撿同
      // 一份舊配置的話，你建幾個就會得到幾個一模一樣的東西，而那正是「多個
      // 桌面」要解決的問題本身。
      return account?.desktop_id ? null : legacy.load()
    },
    save: (snapshot) => mine.save(snapshot),
    clear: () => mine.clear(),
  }
  // 沒有桌面 id 就沒有地方可以同步（例如直接打網址進來的）。桌面照開，只是
  // 變更留在這台機器上——借儲存不借可用性（D33），這裡也一樣。
  return askGate && account.desktop_id ? syncedSession(local) : local
}

/**
 * 存在 QSyn、以本機為快取。
 *
 * 桌面是**那個人的**桌面：他在沒碰過的機器上打開，該看到同一組視窗。那件事本機
 * 儲存做不到，所以真相搬到後端（以 user 為鍵，不是以裝置）。
 *
 * 但**只借儲存，不借可用性**（D33）：本機那份永遠先寫、也永遠是開機時的後備。
 * QSyn 連不上時桌面照開，只是這段時間的變更留在這台機器上。桌面是純前端的東西，
 * 不該因為別人的服務掛了就開不起來。
 *
 * 衝突用 rev 擋。同一個人兩台同時開著時，落後的那一次寫入會被回絕——**它就只是
 * 不送出去**，不重試也不倒帶。下一次真的有變動時會帶著新的 rev 再送，所以不會
 * 兩邊互相蓋來蓋去，代價是那一次的變更只留在本機。
 */
function syncedSession(local: SessionStore): SessionStore {
  let rev = 0
  return {
    async load() {
      const reply = await askGate!({ qsyn_gate_op: 'session.load' })
      if (reply?.ok) {
        rev = Number(reply.rev ?? 0)
        if (typeof reply.widget_limit === 'number') windowLimit = reply.widget_limit
      }

      let remote: SessionSnapshot | null = null
      if (reply?.ok && typeof reply.snapshot === 'string' && reply.snapshot !== '') {
        try {
          remote = JSON.parse(reply.snapshot) as SessionSnapshot
        } catch {
          // 存壞了就當作沒有，別讓一份爛快照擋住開機
        }
      }
      const cached = await local.load()

      // **誰新用誰**，不是伺服器一律優先。
      //
      // 送不出去的那一次存檔（衝突、離線）只留在本機，伺服器上還是舊的。伺服器
      // 無條件贏的話，那一次的變更會在下一次開機時被安靜地丟掉——症狀是「剛剛
      // 做的事重整就沒了」，而且看起來像是根本沒存過。
      const newer =
        remote && cached
          ? (remote.saved_at ?? 0) >= (cached.saved_at ?? 0)
            ? remote
            : cached
          : (remote ?? cached)

      // 本機是開機時的後備，所以拿到比較新的遠端就順手更新它
      if (newer && newer === remote) void local.save(remote)
      return newer
    },
    async save(snapshot) {
      await local.save(snapshot)
      const reply = await askGate!({
        qsyn_gate_op: 'session.save',
        rev,
        snapshot: JSON.stringify(snapshot),
      })
      if (reply?.ok) {
        rev = Number(reply.rev ?? rev)
        return
      }
      // 沒送出去要**說出來**。本機那一份已經寫好了，所以講的是「沒有同步」，
      // 不是「沒有存」——安靜地失敗會讓人以為東西在別的機器上等著他。
      if (reply?.reason === 'conflict') {
        rev = Number(reply.rev ?? rev)
        throw new Error(
          bt({
            'zh-Hant': '這個桌面在別的地方被改過，這一次沒有同步上去',
            en: 'This desktop was changed elsewhere; this save did not sync up',
          }),
        )
      }
      throw new Error(
        bt({
          'zh-Hant': `沒有同步到 QSyn（${reply?.reason ?? '沒有回應'}），變更留在這台機器上`,
          en: `Did not sync to QSyn (${reply?.reason ?? 'no response'}); changes stayed on this device`,
        }),
      )
    },
    async clear() {
      await local.clear()
      const reply = await askGate!({ qsyn_gate_op: 'session.save', rev, snapshot: '' })
      if (reply?.ok) rev = Number(reply.rev ?? rev)
    },
  }
}

/**
 * 叫登入閘把這台裝置的憑證丟掉。
 *
 * 開一個只用一次的閘門 iframe，送一個指令，等它回話。它做的兩件事（伺服器撤銷、
 * 本機刪除）都在那一頁裡，因為那是唯一碰得到那把憑證的地方。
 *
 * **等不到也要往下走。** 登不出去比登出慢還糟：人按了登出，畫面就該離開這個桌
 * 面。連不上的時候伺服器那一份撤銷不掉，但本機這一份會不見——而在一支交給別人
 * 的手機上，本機那一份才是門。
 */
function forgetDevice(gateUrl: string, desktopId: string): Promise<void> {
  return new Promise((resolve) => {
    const url = new URL(gateUrl)
    url.searchParams.set('desktop', location.origin)
    if (desktopId) url.searchParams.set('d', desktopId)

    const frame = document.createElement('iframe')
    frame.src = url.href
    frame.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden'

    let settled = false
    const finish = (): void => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      clearInterval(retry)
      removeEventListener('message', onGate)
      frame.remove()
      resolve()
    }
    const timer = setTimeout(() => {
      console.warn('[qdesktop] logout: the gate did not answer; the local credential may survive')
      finish()
    }, 6000)

    /*
      一直問到它回話為止。

      **不能只在 load 的時候問一次。** 一個剛掛上去的 iframe 會先為 about:blank
      放一個 load 事件，那一刻 contentWindow 還不是閘門那一頁——訊息送進一個等一
      下就會被換掉的文件裡，於是閘門從頭到尾沒收到，而畫面上看起來是「登出了」，
      實際上憑證還在。這個 bug 只在時機湊巧時發作，所以它在測試裡時好時壞。

      重問是安全的：忘記兩次跟忘記一次的結果一樣。
    */
    const ask = (): void => {
      frame.contentWindow?.postMessage({ qsyn_gate_op: 'device.forget', id: 1 }, url.origin)
    }
    const retry = setInterval(ask, 400)

    function onGate(event: MessageEvent): void {
      if (event.origin !== url.origin) return
      const data = event.data as { qsyn_gate?: number; reply_to?: number } | null
      if (data?.qsyn_gate !== 1) return
      // 閘門開口說話了 —— 它的監聽器一定已經掛好，這一次問得到。
      if (data.reply_to !== 1) return ask()
      console.info('[qdesktop] logout: the gate answered')
      finish()
    }
    addEventListener('message', onGate)
    frame.addEventListener('load', ask)
    document.body.append(frame)
  })
}

async function requireLogin(gateUrl: string): Promise<Account> {
  const url = new URL(gateUrl)
  url.searchParams.set('desktop', location.origin)
  // 哪一個桌面。閘門要拿它配對、要拿它存檔——一台裝置是一塊螢幕，一塊螢幕
  // 顯示一個桌面。
  const which = new URLSearchParams(location.search).get('d')
  if (which) url.searchParams.set('d', which)

  /*
    剛從 QSyn 連結回來的話，網址上會有一張兌換券。

    它不是憑證：九十秒後失效，而且只能用一次。真正的 device token 由閘門那一頁
    自己去換——那一步發生在 QSyn 的來源裡，桌面這一側從頭到尾沒有碰過任何憑證。
    這一點沒有因為改用兌換券而鬆動，鬆動的是「靠共用儲存把它傳過去」那個假設。

    立刻從網址上抹掉。留著的話它會進到瀏覽紀錄、也會在重新整理時被再兌一次（那
    一次必定失敗，於是看起來像連結壞了）。replaceState 不會多一筆歷史。
  */
  const claim = new URLSearchParams(location.search).get('qd_pair')
  if (claim) {
    url.searchParams.set('claim', claim)
    const clean = new URL(location.href)
    clean.searchParams.delete('qd_pair')
    history.replaceState(null, '', clean.href)
  }

  const gateOrigin = url.origin

  const frame = document.createElement('iframe')
  frame.src = url.href
  frame.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden'
  document.body.append(frame)

  /*
    在 iOS 上，WKWebView 對第三方 iframe 的 localStorage 是 partitioned +
    ephemeral——寫得下、當下也讀得回來，但 app 一冷啟動整格就沒了。gate iframe
    的 device token 存在那一格，所以每次開 app 都被打回「no-session」，使用者
    被逼著重登。

    解法是把 device token 交給原生那半保管：iOS 的 NSUserDefaults 這樣的存
    儲是跨 app 生命週期存活的，直到使用者主動刪 app。gate 開機時先問殼「有沒有
    給我」（ready），殼從 Preferences 讀出來、postMessage 回去；gate 之後在
    自己那格 localStorage 拿到什麼就同步一份給殼（persist）——**憑證本身仍然只
    住在原生 storage 與 gate iframe 兩處**，其他地方看不到，設計原則沒有鬆動：
    殼只是傳信人，讀到的是 opaque 字串，這件事沒有比 iOS 給它讀 URL query
    參數更危險。

    網頁版與 Android 不會走到這裡——那兩邊 localStorage 本來就撐得住，
    Preferences 不存在時整個橋自動變 no-op。
  */
  const prefs = nativeShell()?.Preferences
  async function loadNative(key: string): Promise<string> {
    if (!prefs) return ''
    try {
      const { value } = await prefs.get({ key })
      return value ?? ''
    } catch {
      return ''
    }
  }
  async function saveNative(key: string, value: string): Promise<void> {
    if (!prefs) return
    try {
      if (value) await prefs.set({ key, value })
      else await prefs.remove({ key })
    } catch {}
  }

  // 每個請求一個編號，回覆照號碼配對。逾時回 null 而不是永遠等下去：
  // 存檔失敗桌面照樣要能用，卡住的 promise 會讓存檔佇列整條停住。
  const waiting = new Map<number, (reply: GateReply | null) => void>()
  let nextId = 1
  askGate = (request) =>
    new Promise<GateReply | null>((settle) => {
      const id = nextId++
      const timer = setTimeout(() => {
        waiting.delete(id)
        settle(null)
      }, 10_000)
      waiting.set(id, (reply) => {
        clearTimeout(timer)
        settle(reply)
      })
      frame.contentWindow?.postMessage({ ...request, id }, gateOrigin)
    })

  const screen = loginScreen(gateOrigin, () => {
    frame.contentWindow?.postMessage({ qsyn_gate_recheck: 1 }, gateOrigin)
  })

  return new Promise<Account>((resolve) => {
    addEventListener('message', (event: MessageEvent) => {
      if (event.origin !== gateOrigin) return
      const data = event.data as
        | (Account & {
            qsyn_gate?: number
            ok?: boolean
            reply_to?: number
            ready?: number
            persist?: number
            token_key?: string
            token?: string
          } & GateReply)
        | null
      if (!data || data.qsyn_gate !== 1) return
      // 兩條原生儲存的橋，只在有 Preferences 時真的動。gate 開機時問「有沒有給我」，
      // gate 自己那格 localStorage 有變動時告訴殼一聲。
      if (data.ready === 1 && typeof data.token_key === 'string') {
        const key = data.token_key
        void loadNative(key).then((token) => {
          frame.contentWindow?.postMessage(
            { qsyn_gate_bootstrap: 1, token_key: key, token },
            gateOrigin,
          )
        })
        return
      }
      if (data.persist === 1 && typeof data.token_key === 'string') {
        void saveNative(data.token_key, typeof data.token === 'string' ? data.token : '')
        return
      }
      if (data.reply_to !== undefined) {
        waiting.get(data.reply_to)?.(data)
        waiting.delete(data.reply_to)
        return
      }
      if (!data.ok) {
        // 要人登入的時候就別再讓開機畫面遮著了——它會蓋住登入按鈕，而且
        // 那個「載入中」的樣子會讓人以為再等一下就好。
        booted()
        screen.show(data.reason)
        return
      }
      screen.hide()
      resolve({
        desktop_id: data.desktop_id ?? '',
        desktop: data.desktop ?? '',
        user_id: data.user_id ?? '',
        user: data.user ?? '',
        email: data.email ?? '',
        workspace: data.workspace ?? '',
        workspace_id: data.workspace_id ?? '',
        paired: data.paired === true,
      })
    })
  })
}

/**
 * 這個桌面是不是跑在一個原生殼裡（Capacitor）。
 *
 * 現在只有兩件事用得到它：收掉啟動畫面，以及讓返回鍵有意義。**登入不用**——
 * 那條路已經跟網頁版合成同一條，見 loginScreen。
 *
 * 用執行期偵測而不是 import：這個 repo 是 0 個 runtime 相依，而為了一個殼把
 * Capacitor 變成建置相依，等於讓每一個部署都背它——包括那些永遠不會做成 app 的。
 * 殼在的時候，它自己會把 `window.Capacitor` 放上來；不在的時候什麼都不會發生。
 *
 * 這跟 `navigator.locks` 是同一種寫法：問環境有沒有這個能力，不是問「我被誰打包」。
 */
function nativeShell(): NativePlugins | null {
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean; Plugins?: unknown } })
    .Capacitor
  if (!cap?.isNativePlatform?.()) return null
  return (cap.Plugins ?? {}) as NativePlugins
}

interface AppPlugin {
  addListener(event: 'appUrlOpen', handler: (data: { url: string }) => void): void
  addListener(event: 'backButton', handler: (data: { canGoBack: boolean }) => void): void
  exitApp(): void
}

interface NativePlugins {
  App?: AppPlugin
  Browser?: { open(options: { url: string }): Promise<void> }
  SplashScreen?: { hide(options?: { fadeOutDuration?: number }): Promise<void> }
  Keyboard?: {
    addListener(
      event: 'keyboardWillShow' | 'keyboardDidShow',
      handler: (info: { keyboardHeight: number }) => void,
    ): void
    addListener(event: 'keyboardWillHide' | 'keyboardDidHide', handler: () => void): void
  }
  StatusBar?: {
    setStyle(options: { style: string }): Promise<void>
    setOverlaysWebView(options: { overlay: boolean }): Promise<void>
    setBackgroundColor(options: { color: string }): Promise<void>
  }
  /**
   * 存活得比 WKWebView 久的一格儲存——iOS 上底下是 NSUserDefaults，Android 是
   * SharedPreferences。給登入閘 iframe 的 device token 用：它自己那一格
   * localStorage 在原生殼裡是 partitioned + ephemeral，app 冷啟動就清了。
   */
  Preferences?: {
    get(options: { key: string }): Promise<{ value: string | null }>
    set(options: { key: string; value: string }): Promise<void>
    remove(options: { key: string }): Promise<void>
  }
}

/** 未登入時蓋住整個畫面的那一層。回傳顯示／隱藏它的兩個把手。 */
function loginScreen(
  gateOrigin: string,
  recheck: () => void,
): { show(reason?: string): void; hide(): void } {
  const layer = document.createElement('div')
  layer.style.cssText =
    'position:fixed;inset:0;z-index:99999;display:none;place-items:center;' +
    'background:#0f1117;color:#e8e8f0;font:14px/1.7 system-ui,sans-serif'
  const card = document.createElement('div')
  card.innerHTML = '<div style="font:600 20px system-ui;margin-bottom:2px">QDesktop</div>'
  card.append(signInPanel('請先登入 QSyn。桌面上的代理人要用你的工作空間才能運作。'))
  const reasonLine = document.createElement('p')
  reasonLine.setAttribute('data-why', '')
  reasonLine.style.cssText =
    'opacity:.35;font-size:11px;margin:12px 0 0;font-family:ui-monospace,monospace;text-align:center'
  card.append(reasonLine)
  layer.append(card)
  document.body.append(layer)

  /*
    原生殼裡開系統瀏覽器（Safari），登完靠 qdesktop://paired 回程；網頁版就地
    跳走。這一顆按鈕不管密碼登入還是 Google，那些選擇都在 QSyn 的登入頁上讓
    使用者自己挑——Safari 又原生支援 iCloud Keychain autofill，殼裡自己刻表
    單只是重蓋一遍那些能力，還蓋得比較差。

    去的是 QSyn 的**連結頁**（/desktop/link）而不是登入頁：連結頁在頂層看得
    到 JWT，登入之後由它負責配對，把兌換券送回來——這件事這個 iframe 做不到
    （partitioned storage 看不到第一方的登入狀態）。連結頁沒登入就自己轉去登
    入再轉回來，登入了就直接配對，所以這個按鈕不管人有沒有登入都是同一顆。
  */
  const shell = nativeShell()
  const primary = layer.querySelector('button')
  primary?.addEventListener('click', () => {
    const which = new URLSearchParams(location.search).get('d') ?? ''
    if (shell) {
      const next = encodeURIComponent(HANDOFF_RETURN)
      void shell.Browser?.open({
        url: `${gateOrigin}/desktop/link?d=${encodeURIComponent(which)}&next=${next}`,
      })
    } else {
      const next = encodeURIComponent(location.href)
      location.assign(`${gateOrigin}/desktop/link?d=${encodeURIComponent(which)}&next=${next}`)
    }
  })

  /*
    閘門說不行的時候，把它說的理由寫出來。

    這片畫面到目前為止是個黑箱：不管是沒登入、憑證過期、還是這個 iframe 根本
    讀不到自己來源的 localStorage（手機上的儲存隔離），使用者看到的都是同一
    句「請先登入」，於是登了又登。三種情況要修的地方完全不同，而分辨它們的資
    訊閘門本來就送過來了，只是沒有人把它顯示出來。

    寫得小、寫得灰：它不是給使用者看的，是給查問題的人看的。
  */
  const why: Record<string, string> = {
    'no-session': '這台裝置還沒有連結 QSyn 帳號',
    'stale-session': 'QSyn 憑證已失效，請重新登入',
    unreachable: '連不上 QSyn',
  }

  // 輪詢留著：人可能是自己在另一個分頁登入的，或是按上一頁回來的。跳轉是主要
  // 的路，這是那條路沒走成時的備援。
  let timer: ReturnType<typeof setInterval> | undefined
  return {
    show(reason?: string) {
      const line = layer.querySelector('[data-why]')
      if (line) line.textContent = reason ? (why[reason] ?? reason) : ''
      if (layer.style.display === 'grid') return
      layer.style.display = 'grid'
      timer ??= setInterval(recheck, 2000)
    },
    hide() {
      layer.remove()
      if (timer) clearInterval(timer)
    },
  }
}

/**
 * 抓不到設定就把原因寫在畫面上。
 *
 * 部署最容易錯的就是這一步（路徑、MIME、快取），留一片空白會很難查。
 */
async function loadConfig(): Promise<DesktopConfig> {
  const url = new URL('./config.json', location.href)
  try {
    const response = await fetch(url, { cache: 'no-cache' })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return (await response.json()) as DesktopConfig
  } catch (cause) {
    const banner = document.createElement('pre')
    banner.style.cssText =
      'margin:0;padding:14px;color:#ff8a80;background:#14141c;font:12px/1.6 ui-monospace,monospace;white-space:pre-wrap'
    banner.textContent = `QDesktop 讀不到設定檔\n${url}\n${String(cause)}`
    document.body.prepend(banner)
    throw cause
  }
}
